import React from "react";
import PropTypes from "prop-types";
import { Button, Modal, Grid, Input, TextArea, Message, Label, Icon } from "semantic-ui-react";
import { observer } from "mobx-react";
import Api from "../service/Api";
import Tour from "../stores/Tour";
import PromiseButton from "../components/element/PromiseButton";
import FocusCheckbox from "../components/element/FocusCheckbox";
import ErrorWrapper from "../components/wrapper/ErrorWrapper";
import LoadingWrapper from "../components/wrapper/LoadingWrapper";
import AnalyticsWrapper from "../components/wrapper/AnalyticsWrapper";
import Analytics from "../service/Analytics";
import FeedbackWidget from "./FeedbackWidget";

function writeEvent(name, data) {
  Analytics.writeEvent(`Room Classifier (${name})`, data);
}

@observer
class RoomClassifierWidget extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      visible: 0,
      showWelcome: true,
      showPicker: false,
      closing: false,
      showFeedback: false,
      choice: "suggested",
      advanced: false,
      name: null,
      description: null,
    };
  }

  onFeedback() {
    const { advanced } = this.state;
    const { redirect } = this.props;

    this.setState({
      showFeedback: false,
    });

    if (advanced) {
      this.setState({
        closing: true,
      });

      if (redirect) {
        window.location.href = redirect;

        return;
      }

      window.location.reload();

      return;
    }

    this.setState({
      showPicker: false,
    });
  }

  onCancel() {
    const { showWelcome, visible } = this.state;

    if (!showWelcome) {
      writeEvent("Cancel", {
        completed: visible,
        total: this.reviewable().length,
      });
    }

    this.onClose();
  }

  onClose() {
    const { advanced } = this.state;

    if (!advanced) {
      // skip feedback window if they did not classify at least one item
      this.onFeedback(false);

      return;
    }

    this.setState({
      showPicker: false,
      showFeedback: true,
    });
  }

  onKeyPress(e) {
    if (e.key === "Enter") {
      this.doAdvance();
    }
  }

  doShowInterface() {
    writeEvent("Show");

    this.setState({
      showPicker: true,
      showFeedback: false,
    });
  }

  doWelcomeContinue() {
    writeEvent("Welcomed");

    this.setState({
      showWelcome: false,
    });
  }

  doGoBack() {
    const { visible } = this.state;
    if (visible <= 0) {
      return;
    }

    const reviewable = this.reviewable();
    const photo = reviewable[visible - 1];

    this.setState({
      visible: visible - 1,
      name: photo.metadata.name,
      choice: photo.metadata.choice,
      description: photo.metadata.description,
    });
  }

  doAdvance() {
    const { visible, name, description, choice } = this.state;
    const { showError } = this.props;
    const reviewable = this.reviewable();
    const photo = reviewable[visible];

    this.state.advanced = true;

    return new Promise((fulfill, reject) => {
      // Existing
      let postName = photo.name;
      let postChoice = -1;

      if (choice === "new") {
        postName = name || photo.classifications[0]; // if the user does not change it, default to classification name
      } else if (choice === "suggested") {
        [postName] = photo.classifications;
        postChoice = 0;
      }

      Api.post(`/provider/photo/${photo.id}`, {
        name: postName,
        description,
        classify_choice: postChoice,
      })
        .then(() => {
          const event = {
            photoId: photo.id,
            choice: postChoice,
            source: photo.source,
            existing: photo.name,
            suggestion: photo.classifications[0],
            name: postName,
          };

          // This data is written to the photo only for the sake of the 'back button' functionality.
          // see goBack() method where they are used.
          photo.metadata = {
            name,
            choice,
            description,
          };

          if (postChoice === -1) {
            event.existing = !(choice === "new");
          }

          if (!this.isLastImage()) {
            this.setState({
              visible: visible + 1,
              choice: "suggested",
              name: null,
              description: null,
            });

            writeEvent("Classified", event);

            fulfill();

            return;
          }

          writeEvent("Classified", event);
          writeEvent("Complete", {
            total: reviewable.length,
          });

          this.onClose();
        })
        .catch(ex => {
          showError();

          reject(ex);
        });
    });
  }

  reviewable() {
    const { store } = this.props;

    return store.photos.filter(photo => photo.classifications && photo.classifications.length > 0);
  }

  isLastImage() {
    const { visible } = this.state;

    // If the user classified all their images
    return visible + 1 === this.reviewable().length;
  }

  render() {
    const { store } = this.props;
    const { showPicker, showWelcome, visible, choice, name, description, closing, showFeedback } = this.state;

    const reviewable = this.reviewable();

    if (reviewable.length === 0) {
      return <div />;
    }

    let content = (
      <div>
        <p>
          The purpose of this automated image naming tool is to not only remove a step in your image processing
          workflow, but allow you to take advantage of the image caption capabilities of our many designs.
        </p>

        <p>
          <strong>Note:</strong> This functionality is currently in an experimental phase and may be removed at any
          time. Your participation will allow us to gather data to help improve the accuracy of our algorithm.
        </p>
      </div>
    );

    let button = (
      <Button
        icon="chevron right"
        color="blue"
        labelPosition="right"
        content="Continue"
        basic
        onClick={() => this.doWelcomeContinue()}
      />
    );

    if (!showWelcome) {
      const photo = reviewable[visible];

      button = [
        <PromiseButton
          key="advance"
          icon="chevron right"
          color="green"
          basic={!this.isLastImage()}
          content={this.isLastImage() ? "Finish" : "Continue"}
          labelPosition="right"
          onClick={() => this.doAdvance()}
          disabled={choice == null}
        />,
      ];

      // if it's not the first photo
      if (visible > 0) {
        button.unshift(
          <Button
            key="previous"
            icon="chevron left"
            color="blue"
            labelPosition="left"
            content="Previous"
            basic
            onClick={() => this.doGoBack()}
          />,
        );
      }

      if (!this.isLastImage()) {
        button.unshift(
          <PromiseButton
            key="skip"
            color="grey"
            basic={!this.isLastImage()}
            content="Finish Later"
            onClick={() => this.onCancel()}
          />,
        );
      }

      const options = [
        {
          value: "existing",
          label: "Existing",
          content: photo.name,
        },
        {
          value: "suggested",
          label: "Suggested",
          content: photo.classifications[0],
        },

        {
          value: "new",
          label: "New",
          content: (
            <Input
              key={`input${visible}`}
              value={name === null ? photo.classifications[0] : name}
              onChange={(event, data) => {
                this.setState({
                  name: data.value,
                  choice: "new",
                });
              }}
            />
          ),
        },
      ];

      const descriptionInput = (
        <TextArea
          id="description"
          style={{
            height: 100,
          }}
          value={description || photo.description}
          onChange={(event, data) =>
            this.setState({
              description: data.value,
            })
          }
        />
      );

      const descriptionLabel = <label htmlFor="description">Description</label>;

      content = (
        <div onKeyPress={e => this.onKeyPress(e)} key={`div${visible}`}>
          <Grid>
            <Grid.Column width={8} textAlign="center">
              <img className="ui small image" src={photo.large2} style={{ width: "100%" }} />
              <div
                style={{
                  paddingTop: 10,
                }}
              >
                Photo {visible + 1} / {reviewable.length}
              </div>
            </Grid.Column>
            <Grid.Column width={8}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  lineHeight: "30px",
                }}
              >
                {options.map(option => (
                  <div
                    key={option.value}
                    style={{
                      display: "flex",
                      alignItems: "baseline",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      this.setState({
                        choice: option.value,
                      })
                    }
                  >
                    <div>
                      <FocusCheckbox name="choice" radio value={option.value} checked={choice === option.value} />
                    </div>
                    <div
                      style={{
                        width: 100,
                        paddingLeft: 5,
                      }}
                    >
                      <strong>{option.label}</strong>
                    </div>
                    <div>{option.content}</div>
                  </div>
                ))}
                <div
                  key="description"
                  style={{
                    display: "flex",
                    marginLeft: 12,
                    marginTop: 20,
                  }}
                >
                  <div
                    style={{
                      width: 100,
                      paddingLeft: 5,
                    }}
                  >
                    <strong>{descriptionLabel}</strong>
                  </div>
                  <div>{descriptionInput}</div>
                </div>
              </div>
            </Grid.Column>
          </Grid>
        </div>
      );
    }

    return (
      <LoadingWrapper loading={store.loading || closing}>
        <AnalyticsWrapper visible="Room Classifier Affordance">
          <Message color="orange" className="tb newfeature absolute" style={{ textAlign: "center" }}>
            <Message.Header>
              <Label color="orange" ribbon size="large">
                <Icon name="lab" />
                Experimental
              </Label>
              Deliver more value with automatic image naming!
            </Message.Header>
            <p>
              Using our powerful machine learning technology, we can detect what area of a property your photo belongs
              to and suggest a name to use.
            </p>
            <p>
              <strong>Note:</strong> Trying this feature out will not make any automatic changes to your tour.
            </p>
            <Button color="orange" content="Try Now" onClick={() => this.doShowInterface()} />
          </Message>
        </AnalyticsWrapper>
        <FeedbackWidget show={showFeedback} onClose={() => this.onFeedback()} remember="RoomClassifierWidget" />
        <Modal open={showPicker && !showFeedback} size="small" closeIcon="close" onClose={() => this.onCancel()}>
          <Modal.Header>
            <Label color="orange" ribbon size="large">
              <Icon name="lab" />
              Experimental
            </Label>
            Automatic Room Namer
          </Modal.Header>
          <Modal.Content>{content}</Modal.Content>
          <Modal.Actions>{button}</Modal.Actions>
        </Modal>
      </LoadingWrapper>
    );
  }
}

RoomClassifierWidget.propTypes = {
  redirect: PropTypes.string.isRequired,
  store: PropTypes.instanceOf(Tour),
};

export default ErrorWrapper(RoomClassifierWidget);
